import React from 'react'
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  styled,
} from '@mui/material'
import { CampaignSetupFormValues, CreativeType, CtaType } from '../types'
import StepNavigation from './StepNavigation'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
  color: theme.palette.secondary.dark,
}))

interface Props {
  values: CampaignSetupFormValues
  onChange: (field: keyof CampaignSetupFormValues, value: unknown) => void
  onNext: () => void
  onBack: () => void
  jobName?: string
  readOnly?: boolean
}

// Carousel is offered by the data model but no carousel creative is built on publish —
// it goes out as a single image. Label it so nobody expects multiple cards; existing
// carousel drafts still publish exactly as they did before.
const CREATIVE_TYPES: { value: CreativeType; label: string }[] = [
  { value: 'SINGLE_IMAGE', label: 'Single Image' },
  { value: 'SINGLE_VIDEO', label: 'Single Video' },
  { value: 'CAROUSEL', label: 'Carousel (publishes as a single image)' },
]

const CTA_TYPES: { value: CtaType; label: string }[] = [
  { value: 'APPLY_NOW', label: 'Apply Now (APPLY_NOW)' },
  { value: 'LEARN_MORE', label: 'Learn More (LEARN_MORE)' },
  { value: 'SIGN_UP', label: 'Sign Up (SIGN_UP)' },
  { value: 'SUBSCRIBE', label: 'Subscribe (SUBSCRIBE)' },
  { value: 'GET_QUOTE', label: 'Get Quote (GET_QUOTE)' },
]

const AdCreativeStep: React.FC<Props> = ({
  values,
  onChange,
  onNext,
  onBack,
  jobName,
  readOnly,
}) => {
  // Mirrors the server-side check in util/socialCreativeMedia — a format that disagrees
  // with the uploaded media is rejected at publish, so surface it while it can be fixed.
  const wantsVideo = values.creativeType === 'SINGLE_VIDEO'
  const hasImage = values.mediaAssets.some((a) => a.type === 'image')
  const mediaMatchesType = wantsVideo
    ? values.mediaAssets.some((a) => a.type === 'video')
    : hasImage

  // For video, an image in the Media step is the thumbnail, not a second ad — worth
  // saying here because this is where the video is chosen.
  const mediaSummary = (() => {
    if (!wantsVideo) return 'Uses the image uploaded in the Media step.'
    return hasImage
      ? 'Uses the video from the Media step, with the uploaded image as its thumbnail.'
      : 'Uses the video from the Media step. Meta picks the thumbnail automatically — upload an image as well to choose it yourself.'
  })()

  const mediaMissing = `No ${
    wantsVideo ? 'video' : 'image'
  } uploaded — add one in the Media step or publishing will fail.`

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Ad Creative
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            sct_AD_ACCOUNT_ID_#n-adcreatives
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Ad Name *"
                value={values.adName}
                onChange={(e) => onChange('adName', e.target.value)}
                placeholder={`Creative - ${jobName || 'Sales Manager'} Image Ad`}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Creative Type *</InputLabel>
                <Select
                  value={values.creativeType}
                  label="Creative Type *"
                  onChange={(e) => onChange('creativeType', e.target.value)}
                >
                  {CREATIVE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color={mediaMatchesType ? 'text.secondary' : 'error'}
                sx={{ display: 'block', mt: 0.5 }}
              >
                {mediaMatchesType ? mediaSummary : mediaMissing}
              </Typography>
            </Grid>
          </Grid>

          {/* Creative Payload */}
          <SubSectionTitle>Creative Payload (link_data)</SubSectionTitle>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                label="Primary Text (message) *"
                value={values.primaryText}
                onChange={(e) => onChange('primaryText', e.target.value)}
                placeholder="We are looking for a Sales Manager in Berlin. Apply now in just 2 minutes!"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Headline (name) *"
                value={values.headline}
                onChange={(e) => onChange('headline', e.target.value)}
                placeholder={`${jobName || 'Sales Manager'} (m/f/d) wanted`}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={values.description}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder="Apply now in just 2 minutes!"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Website URL (link) *"
                value={values.websiteUrl}
                onChange={(e) => onChange('websiteUrl', e.target.value)}
                placeholder="https://example.com/careers"
                disabled={readOnly}
              />
            </Grid>
          </Grid>

          {/* Call to Action */}
          <SubSectionTitle>Call to Action</SubSectionTitle>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>CTA Type *</InputLabel>
                <Select
                  value={values.ctaType}
                  label="CTA Type *"
                  onChange={(e) => onChange('ctaType', e.target.value)}
                >
                  {CTA_TYPES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                The call_to_action will be linked to the lead form created in the previous step.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} />
    </Box>
  )
}

export default AdCreativeStep
